import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { Header } from "../../../components/Header";
import CustomDrawer from "../../../components/Drawer/index.js";
import CustomPopup from "../../../components/CustomPopup";
import Accordian from "../../../components/Accordian";
import { PopUp } from "../../../components/index.js";
import { useMobileStyles } from "./styles.js";
import Icon from "../../../utils/icons";
import { isWeb, getAirUnit } from "../../../utils/utility";
import ToastMessage from "../../../components/ToastMessage/index.js";
import ErrorCard from "../../../components/ErrorCard/index.js";
import { PAGES, ENERGY_SOURCE, APP_SOURCE, MODES } from "../../../utils/constant";
import I18n from "../../../localization";
import { infoIconData } from './util';
import EnergyPagePopup from "../../../components/EnergyModePopup/index.js";
import HCServices from "../../../services/hcServices.js";
import { frameSchedulerDataForSaving } from "../IQAirScheduler/utils/helpers";

// Import custom hooks
import { useIQAirData, useIQAirSettings, useIQAirSlider } from './hooks';

// Import components
import { EnergyModeSelector, TemperatureControl, FanModeSelector, DeviceNameInput, AirModeSelector, SmartModeControls, ScheduleScenes } from './components';
import EditScenePopup from './components/EditScenePopup';
import IQAirScheduler from '../IQAirScheduler';

export default function IQAirSetting(props) {
  const classes = useMobileStyles();
  
  const ENERGY_MODES = {
    MANUAL: I18n.t("mobile.iqAirSetting.modes.manualMode"),
    SMART: I18n.t("mobile.iqAirSetting.modes.smartMode"),
    SCHEDULE: I18n.t("mobile.iqAirSetting.modes.scheduledMode"),
  };

  // State management
  const [toast, setToast] = useState({
    status: false,
    message: null,
    type: "success",
    isPermissionDeniedMsg: false
  });
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [showEnergyModePopup, setShowEnergyModePopup] = useState(false);
  const [showEditScenePopup, setShowEditScenePopup] = useState(false);
  const [showSchedulerPopup, setShowSchedulerPopup] = useState(false);
  const [showDiscardPopup, setShowDiscardPopup] = useState(false);
  const [isApplyInProgress, setIsApplyInProgress] = useState(false);
  const [selectedScene, setSelectedScene] = useState(null);
  const [localPresets, setLocalPresets] = useState([]);
  const [pendingSceneUpdates, setPendingSceneUpdates] = useState({});
  const [popupScheduleData, setPopupScheduleData] = useState(null);
  const [scheduleBaselineData, setScheduleBaselineData] = useState(null);
  const [isScheduleDirty, setIsScheduleDirty] = useState(false);
  const [schedulerSessionId, setSchedulerSessionId] = useState(0);
  const [comfortRange, setComfortRange] = useState({ cool_setpoint: null, heat_setpoint: null });
  const [sliderMarksArray, setSliderMarksArray] = useState({ cool_setpoint: null, heat_setpoint: null });
  const hasInitializedTempStateRef = useRef(false);

  const inputCardClass = [classes.IQCard];

  // Custom hooks
  const { loading, iqAirDetails, setIqAirDetails, availableModes, energySource, IQAirRange, IQAirTemperatureRange, IQAirComfortRange, setIQAirComfortRange, isThermostatSmartModeAllowed, isIQAirOnly, headerName, setHeaderName, airDeviceName, siteId, iqAirId, IQAirPoints, setIQAirPoints, marksArray, setMarksArray, range, setRange, manualProfile, smartProfile, energyMode, setEnergyMode, presets, setPresets , apiError, scheduleData ,setScheduleData, initializeData } = useIQAirData(props.isDarkMode);

  const { selectedValue, setSelectedValue, handleConfirmSelection, validateAndApplyModeChange, handleComfortRangeChange, updateSliderMarksAndRange } = useIQAirSlider(IQAirPoints, setIQAirPoints, IQAirComfortRange, setIQAirComfortRange, iqAirDetails, availableModes, setComfortRange, setSliderMarksArray, IQAirRange, setIqAirDetails);
  const safeSelectedValue = selectedValue && typeof selectedValue === 'object' ? selectedValue : { local: '' };
  const selectedEnergyMode = safeSelectedValue.local || '';

  const { savingLoader, IQAirNameError, isPageDataChanged, handleNameChange, setIQAirSettings, setTempIqAirDetails, setTempIQAirPoints, setTempSelectedValue, setTempRange, setIsRefreshed } = useIQAirSettings({ iqAirDetails, setIqAirDetails, IQAirPoints, selectedValue: safeSelectedValue, range: comfortRange || { cool_setpoint: null, heat_setpoint: null }, availableModes, hasAccess: props.hasAccess, maskedOwnerEmail: props.maskedOwnerEmail, siteId, iqAirId, setToast, setHeaderName, loading });

  useEffect(() => {
    hasInitializedTempStateRef.current = false;
  }, [siteId, iqAirId]);

  // Initialize temp values when data is loaded (only once per device load)
  useEffect(() => {
    if (loading || hasInitializedTempStateRef.current) {
      return;
    }

    setTempIqAirDetails(iqAirDetails);
    setTempIQAirPoints(IQAirPoints);

    // Initialize comfortRange and tempRange with the initial range values from API
    if (range) {
      const { cool_setpoint: coolSetpointRange, heat_setpoint: heatSetpointRange } = range;
      if (Array.isArray(coolSetpointRange) && Array.isArray(heatSetpointRange)) {
        setComfortRange(range);
        setTempRange(range);
      }
    }

    // Initialize sliderMarksArray with the initial marksArray values from API
    if (marksArray) {
      const { cool_setpoint: coolSetpointMarks, heat_setpoint: heatSetpointMarks } = marksArray;
      if (Array.isArray(coolSetpointMarks) && Array.isArray(heatSetpointMarks)) {
        setSliderMarksArray(marksArray);
      }
    }

    // Use API energy mode instead of defaulting to Manual
    let initialEnergyMode = '';
    if (energyMode === 'SMART') {
      initialEnergyMode = I18n.t("mobile.iqAirSetting.energyMode.smartMode");
    } else if (energyMode === 'MANUAL') {
      initialEnergyMode = I18n.t("mobile.iqAirSetting.energyMode.manualMode");
    } else if (energyMode === 'SCHEDULE') {
      initialEnergyMode = I18n.t("mobile.iqAirSetting.modes.scheduledMode");
    }

    if (selectedEnergyMode === "") {
      setSelectedValue({ local: initialEnergyMode });
    }
    setTempSelectedValue({ local: initialEnergyMode });
    hasInitializedTempStateRef.current = true;
  }, [loading, iqAirDetails, IQAirPoints, selectedEnergyMode, energyMode, range, marksArray, setSelectedValue, setTempIqAirDetails, setTempIQAirPoints, setTempRange, setTempSelectedValue, siteId, iqAirId]);

  // tempRange should only be updated when applying settings, not when comfortRange changes
  // This allows isStateChanged to detect changes properly

  useEffect(() => {
    if (headerName && isWeb() && props.updateHeaderText) {
      function getTitle() {
        return (
          <div className={classes.iqAirTitleContainer}>
            <span>{headerName || ''}</span>
            {airDeviceName && <span>{airDeviceName}</span>}
          </div>
        );
      }
      props.updateHeaderText(getTitle());
    }
  }, [headerName, airDeviceName, props.updateHeaderText]);

  useEffect(() => {
    if (Object.keys(pendingSceneUpdates).length === 0) {
      setLocalPresets(presets || []);
    }
  }, [presets, pendingSceneUpdates]);

  useEffect(() => {
    if (!scheduleData) return;

    if (!scheduleBaselineData || !isScheduleDirty) {
      setScheduleBaselineData(scheduleData);
      setIsScheduleDirty(false);
    }

    if (!showSchedulerPopup) {
      setPopupScheduleData(scheduleData);
    }
  }, [scheduleData, scheduleBaselineData, showSchedulerPopup, isScheduleDirty]);

  const areSchedulesEqual = (left, right) => {
    if (!left || !right) return false;
    return JSON.stringify(left) === JSON.stringify(right);
  };

  const openSchedulerPopup = () => {
    setPopupScheduleData(scheduleData);
    setSchedulerSessionId((prev) => prev + 1);
    setShowSchedulerPopup(true);
  };

  const handleSchedulerPopupClose = () => {
    const hasUpdatedSchedule = popupScheduleData && scheduleData && !areSchedulesEqual(popupScheduleData, scheduleData);

    if (hasUpdatedSchedule) {
      setScheduleData(popupScheduleData);
      if (scheduleBaselineData) {
        setIsScheduleDirty(!areSchedulesEqual(popupScheduleData, scheduleBaselineData));
      } else {
        setIsScheduleDirty(true);
      }
    }

    setShowSchedulerPopup(false);
  };

  const handleEnergyModeChange = (e, smartDisabled, controlType) => {
    if (controlType === ENERGY_MODES.SMART && smartDisabled) {
      setShowEnergyModePopup(true);
      return;
    }
    const nextMode = e.target.value;

    if (nextMode === ENERGY_MODES.SMART && smartProfile) {
      setIQAirPoints(smartProfile.setPoints);
      setIQAirComfortRange(smartProfile.comfortRange);
      setSliderMarksArray(smartProfile.marksArray);
      setComfortRange(smartProfile.range);
      setRange(smartProfile.range);
    } else if ((nextMode === ENERGY_MODES.MANUAL || nextMode === ENERGY_MODES.SCHEDULE) && manualProfile) {
      setIQAirPoints(manualProfile.setPoints);
      setIQAirComfortRange(manualProfile.comfortRange);
      setSliderMarksArray(manualProfile.marksArray);
      setComfortRange(manualProfile.range);
      setRange(manualProfile.range);
    }

    setSelectedValue((prev) => ({
      ...prev,
      local: nextMode,
    }));
  };

  const handleFanModeChange = (mode) => {
    setIqAirDetails({ ...iqAirDetails, fanMode: mode });
  };

  const handleSceneClick = (scene) => {
    setSelectedScene(scene);
    setShowEditScenePopup(true);
  };

  const handleEditSceneClose = (updatedSceneData = null) => {
    if (selectedScene && updatedSceneData) {
      const presetId = selectedScene['preset-id'];
      const updatedScene = {
        ...selectedScene,
        ...updatedSceneData,
      };

      setLocalPresets((prevPresets) =>
        (prevPresets || []).map((preset) =>
          preset['preset-id'] === presetId ? updatedScene : preset
        )
      );

      setPendingSceneUpdates((prev) => ({
        ...prev,
        [presetId]: updatedScene,
      }));
    }

    setShowEditScenePopup(false);
    setSelectedScene(null);
  };

  const hasPendingSceneChanges = useMemo(() => Object.keys(pendingSceneUpdates).length > 0, [pendingSceneUpdates]);
  const hasUnsavedChanges = useMemo(() => (
    isPageDataChanged || hasPendingSceneChanges || isScheduleDirty
  ), [isPageDataChanged, hasPendingSceneChanges, isScheduleDirty]);

  const navigateBack = () => {
    props.handleClick(PAGES.AIR_SYSTEM_SETTING, PAGES.IQ_AIR_SETTING);
  };

  const handleBackClick = () => {
    if (hasUnsavedChanges) {
      setShowDiscardPopup(true);
      return;
    }
    navigateBack();
  };

  const handleApplySettings = async () => {
    if (isApplyInProgress || savingLoader) {
      return;
    }

    if (!props.hasAccess) {
      await setIQAirSettings();
      return;
    }

    setIsApplyInProgress(true);
    let didApplyScheduledData = false;
    let didApplySettings = false;

    try {
      const isScheduledModeSelected = selectedEnergyMode === ENERGY_MODES.SCHEDULE;
      const pendingScenes = Object.values(pendingSceneUpdates);

      if (isScheduledModeSelected && pendingScenes.length > 0) {
        didApplyScheduledData = true;
        const successfulSceneIds = [];
        let hasSceneUpdateFailure = false;

        for (const scene of pendingScenes) {
          const payload = {
            "fan-mode": scene['fan-mode'],
            "temperature-mode": scene['temperature-mode']
          };

          if (Object.prototype.hasOwnProperty.call(scene, 'heat-setpoint')) {
            payload["heat-setpoint"] = scene['heat-setpoint'];
          }

          if (Object.prototype.hasOwnProperty.call(scene, 'cool-setpoint')) {
            payload["cool-setpoint"] = scene['cool-setpoint'];
          }

          if (scene['editable-name']) {
            payload.name = scene.name;
          }

          try {
            const result = await HCServices.updateCustomScheduleScene(siteId, iqAirId, scene['preset-id'], payload);
            const isSuccess = result.status === 'SUCCESS' ||
              result.status === 'success' ||
              (result.data && result.status !== 'FAILURE') ||
              (result.status === undefined && result.data);

            if (isSuccess) {
              successfulSceneIds.push(scene['preset-id']);
            } else {
              hasSceneUpdateFailure = true;
            }
          } catch (error) {
            hasSceneUpdateFailure = true;
          }
        }

        if (successfulSceneIds.length > 0) {
          const successfulSceneIdSet = new Set(successfulSceneIds);

          setPresets((prevPresets) =>
            (prevPresets || []).map((preset) => {
              const presetId = preset['preset-id'];
              if (!successfulSceneIdSet.has(presetId) || !pendingSceneUpdates[presetId]) {
                return preset;
              }
              return {
                ...preset,
                ...pendingSceneUpdates[presetId],
              };
            })
          );

          setPendingSceneUpdates((prev) => {
            const next = { ...prev };
            successfulSceneIds.forEach((id) => {
              delete next[id];
            });
            return next;
          });
        }

        if (hasSceneUpdateFailure) {
          setToast({
            status: true,
            message: I18n.t("mobile.iqAirSetting.sceneUpdateFailed"),
            type: "error",
            isPermissionDeniedMsg: false
          });
          return;
        }
      }

      if (isScheduledModeSelected && isScheduleDirty && scheduleData) {
        didApplyScheduledData = true;
        try {
          const payload = frameSchedulerDataForSaving(scheduleData);
          const result = await HCServices.setCustomSchedules(payload, siteId, iqAirId);
          const isSuccess = result.status === 'SUCCESS' ||
            result.status === 'success' ||
            (result.data && result.status !== 'FAILURE') ||
            (result.status === undefined && result.data);

          if (!isSuccess) {
            throw new Error('Schedule save failed');
          }

          setScheduleBaselineData(scheduleData);
          setIsScheduleDirty(false);
        } catch (error) {
          setToast({
            status: true,
            message: I18n.t("mobile.iqAirSetting.scheduleUpdateFailed"),
            type: "error",
            isPermissionDeniedMsg: false
          });
          return;
        }
      }

      if (isPageDataChanged) {
        didApplySettings = await setIQAirSettings();
      } else if (didApplyScheduledData) {
        setToast({
          status: true,
          message: I18n.t('mobile.iqAirSetting.messages.success'),
          type: 'success',
          isPermissionDeniedMsg: false
        });
      }

      if (didApplySettings || didApplyScheduledData) {
        setShowDiscardPopup(false);
      }
    } finally {
      setIsApplyInProgress(false);
    }
  };

  const handleSliderChange = (val, config) => {
    const values = Array.isArray(val) ? val : [val];

    if (config.isAutoMode) {
      let [heat, cool] = values;

      if (heat < config.min) heat = config.min;
      if (cool > config.max) cool = config.max;
      if (config.isDualMode && cool - heat < config.minDistance) return;

      setIQAirPoints((prev) => ({
        ...prev,
        heat_setpoint: heat,
        cool_setpoint: cool,
      }));
    }

    if (config.isHeatMode) {
      let [heat] = values;
      if (heat < config.min) heat = config.min;
      setIQAirPoints((prev) => ({
        ...prev,
        heat_setpoint: heat,
      }));
    }

    if (config.isCoolMode) {
      let [cool] = values;
      if (cool > config.max) cool = config.max;
      setIQAirPoints((prev) => ({
        ...prev,
        cool_setpoint: cool,
      }));
    }
  };

  const handleSliderAfterChange = () => {
    updateSliderMarksAndRange(IQAirPoints);
  };

  // Memoized calculations
  const isSmartModeDisabled = useMemo(() => {
    return !isThermostatSmartModeAllowed || energySource === ENERGY_SOURCE.GAS;
  }, [isThermostatSmartModeAllowed, energySource]);

  const shouldForceDualMode = useMemo(() => {
    return Array.isArray(availableModes) &&
      availableModes.includes('HEAT') &&
      availableModes.includes('COOL');
  }, [availableModes]);

  const scheduleLegendItems = useMemo(() => {
    if (!scheduleData) return [];

    const localPresetNameMap = new Map(
      (localPresets || []).map((preset) => [preset?.['preset-id'], preset?.name])
    );

    const dayOrder = Object.keys(scheduleData)
      .map((dayKey) => Number(dayKey))
      .filter((dayKey) => !Number.isNaN(dayKey))
      .sort((a, b) => a - b);

    const seenPresetKeys = new Set();
    const legendItems = [];

    dayOrder.forEach((dayKey) => {
      const dayBlocks = Array.isArray(scheduleData[dayKey]) ? [...scheduleData[dayKey]] : [];
      dayBlocks
        .sort((leftBlock, rightBlock) => (leftBlock?.start ?? 0) - (rightBlock?.start ?? 0))
        .forEach((block) => {
          const blockName = block?.name;
          const blockColor = block?.color;
          const presetKey = block?.presetId || blockName;
          const resolvedName = localPresetNameMap.get(block?.presetId) || blockName;

          if (!presetKey || !resolvedName || seenPresetKeys.has(presetKey)) {
            return;
          }

          seenPresetKeys.add(presetKey);
          legendItems.push({
            key: presetKey,
            name: resolvedName,
            color: blockColor,
          });
        });
    });

    return legendItems;
  }, [scheduleData, localPresets]);

  return (
    <>
      <div className={classes.IQAirNameContainer}>
        {!props.isWeb && !apiError && <Header
          title={`${headerName || ''}`}
          onClick={handleBackClick}
          label={airDeviceName}
        />
        }
        {loading ? (
          <Box className={classes.loaderContainer}>
            <Icon src="loaderV4" width={"60px"} height={"60px"} />
          </Box>
        ) : apiError ? (
          <ErrorCard
            onGoBack={() => props.handleClick(PAGES.AIR_SYSTEM_SETTING, PAGES.IQ_AIR_SETTING)}
            fetchAirDeviceById={initializeData}
            source={APP_SOURCE.ENHO}
          />
        ) : (
          <div className={classes.IQAirNameInput}>
            <DeviceNameInput
              iqAirDetails={iqAirDetails}
              onNameChange={handleNameChange}
              IQAirNameError={IQAirNameError}
            />

            <div className={classes.cardWithLabel}>
              <Box className={classes.energyModesLearnMoreCard} onClick={() => setIsInfoOpen(true)}>
                <Box className={classes.energyModesLearnMoreContent}>
                  <Icon src="Help" className={classes.energyModesLearnMoreIcon} />
                  <Typography className={classes.energyModesLearnMoreText}>
                    {I18n.t("mobile.iqAirSetting.learnMoreAboutEnergyModes")}
                  </Typography>
                </Box>
              </Box>
            </div>

            <div className={classes.cardWithLabel}>
              <div className={classes.listCardRadio}>
                <Typography className={classes.cardCaption}>{I18n.t('mobile.iqAirSetting.iqAirEnergyMode')}</Typography>
            {/* {    <span className={classes.infoIcon} onClick={() => setIsInfoOpen(true)}>
                  <Icon src="infoIcon" />
                </span>} */}
              </div>
              <Box className={inputCardClass.join(" ")}>
                <EnergyModeSelector
                  controlType={ENERGY_MODES.SMART}
                  subTitle={I18n.t("mobile.energyMode.useAi")}
                  controlTypeSubHeading={I18n.t("mobile.energyMode.comfortLimit")}
                  unit={isWeb() ? getAirUnit() : null}
                  smartDisabled={isSmartModeDisabled}
                  selectedValue={safeSelectedValue}
                  loading={loading}
                  onEnergyModeChange={handleEnergyModeChange}
                >
                  {/* {<AirModeSelector
                    availableModes={availableModes}
                    iqAirDetails={iqAirDetails}
                    onModeChange={validateAndApplyModeChange}
                    isDarkMode={props.isDarkMode}
                    iqAirId={iqAirId}
                  />} */}
                  <TemperatureControl
                    iqAirDetails={iqAirDetails}
                    IQAirPoints={IQAirPoints}
                    IQAirRange={IQAirRange}
                    IQAirTemperatureRange={IQAirTemperatureRange}
                    availableModes={availableModes}
                    forceDualMode={shouldForceDualMode}
                    isDarkMode={props.isDarkMode}
                    onSliderChange={(...args) => {
                      handleSliderChange(...args);
                      handleSliderAfterChange();
                    }}
                    onSliderAfterChange={handleSliderAfterChange}
                  />
                  
                  <SmartModeControls
                    iqAirDetails={iqAirDetails}
                    IQAirPoints={IQAirPoints}
                    availableModes={availableModes}
                    marksArray={sliderMarksArray}
                    range={comfortRange}
                    onComfortRangeChange={handleComfortRangeChange}
                    isDarkMode={props.isDarkMode}
                  />
                  
                  {/* {<FanModeSelector
                    iqAirDetails={iqAirDetails}
                    onFanModeChange={handleFanModeChange}
                    isDarkMode={props.isDarkMode}
                  />} */}
                </EnergyModeSelector>
              </Box>
            </div>

            <div className={classes.cardWithLabel}>
              <Box className={inputCardClass.join(" ")}>
                <EnergyModeSelector
                  controlType={ENERGY_MODES.SCHEDULE}
                  subTitle={I18n.t("mobile.iqAirSetting.scheduleModeDescription")}
                  controlTypeSubHeading=""
                  unit={null}
                  smartDisabled={isSmartModeDisabled}
                  selectedValue={safeSelectedValue}
                  loading={loading}
                  onEnergyModeChange={handleEnergyModeChange}
                >
                  <ScheduleScenes 
                    presets={localPresets}
                    isDarkMode={props.isDarkMode}
                    onSceneClick={handleSceneClick}
                  />
                  <br />

                  <Box className={classes.schedulesContainer} 
                    >
                      <div className={classes.schedulesBox} >
                         <Typography className={classes.scenesSectionTitle}>
                      {I18n.t("mobile.iqAirSetting.schedules")}
                    </Typography>
                    <span onClick={openSchedulerPopup} className={classes.roundedButton}>
                      {I18n.t("mobile.iqAirSetting.edit")}
                    </span>
                      </div>
                   
                    <IQAirScheduler
                        {...props}
                        scheduleData={scheduleData}
                        isReadOnlyMode
                        handleClick={() => setShowSchedulerPopup(false)}
                      />
                    {scheduleLegendItems.length > 0 && (
                      <Box className={classes.scheduleLegendContainer}>
                        {scheduleLegendItems.map((legendItem) => (
                          <Box
                            key={legendItem.key}
                            className={`${classes.scheduleLegendItem} ${classes.scheduleLegendItemThreePerRow}`}
                          >
                            <span
                              className={classes.scheduleLegendSwatch}
                              style={{ background: legendItem.color || '#DFDFDF' }}
                            />
                            <Typography className={classes.scheduleLegendText}>{legendItem.name}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                    </Box>
                    
                </EnergyModeSelector>
              </Box>
            </div>

            <div className={classes.cardWithLabel}>
              <Box className={inputCardClass.join(" ")}>
                <EnergyModeSelector
                  controlType={ENERGY_MODES.MANUAL}
                  subTitle={I18n.t("mobile.energyMode.useThermo")}
                  controlTypeSubHeading=""
                  unit={null}
                  smartDisabled={isSmartModeDisabled}
                  selectedValue={safeSelectedValue}
                  loading={loading}
                  onEnergyModeChange={handleEnergyModeChange}
                >
                  <Box className={classes.popupSection}>
                    <AirModeSelector
                      availableModes={availableModes}
                      iqAirDetails={iqAirDetails}
                      onModeChange={validateAndApplyModeChange}
                      isDarkMode={props.isDarkMode}
                      iqAirId={iqAirId}
                    />
                  </Box>
                  
                  {iqAirDetails.airMode !== MODES.OFF && (
                    <Box className={classes.popupSection}>
                    <TemperatureControl
                      iqAirDetails={iqAirDetails}
                      IQAirPoints={IQAirPoints}
                      IQAirRange={IQAirRange}
                      IQAirTemperatureRange={IQAirTemperatureRange}
                      availableModes={availableModes}
                      isDarkMode={props.isDarkMode}
                      onSliderChange={(...args) => {
                        handleSliderChange(...args);
                        handleSliderAfterChange();
                      }}
                      onSliderAfterChange={handleSliderAfterChange}
                    />
                  </Box>
                  ) }
                  <Box className={classes.popupSection}>
                    <FanModeSelector
                      iqAirDetails={iqAirDetails}
                      onFanModeChange={handleFanModeChange}
                      isDarkMode={props.isDarkMode}
                    />
                  </Box>
                </EnergyModeSelector>
              </Box>
            </div>

            <Box className={classes.iqAirSettingFooter}>
              {/* <div className={`${classes.enableButton} ${classes.button}`} onClick={() => props.handleClick(PAGES.IQ_AIR_SCHEDULER, PAGES.IQ_AIR_SETTING)}>
                Add Schedule
              </div> */}
              <div className={`${(hasUnsavedChanges && !IQAirNameError.status) ? classes.enableButton : ''} ${classes.button}`} style={{marginBottom:'16px'}} onClick={handleApplySettings}>
                {(savingLoader || isApplyInProgress) ? <Icon stroke='#fff' strokeWidth="8px" radius="35" width={"15px"} height={"15px"} src='loaderV4'/> : I18n.t('mobile.iqAirSetting.apply')}
              </div>
            </Box>
          </div>
        )}
        
        <PopUp
          isWeb={props.isWeb}
          open={isInfoOpen}
                     title={props.isWeb ?I18n.t("mobile.energyMode.infoTitle"):null}
          handleClose={() => setIsInfoOpen(false)}
          content={
            props.isWeb ?
                          <Accordian isWeb={true} items={infoIconData()}/>
              : <CustomDrawer
                dismiss={() => setIsInfoOpen(false)}
                message={
                  <Accordian isDarkMode={props.isDarkMode} items={infoIconData()}/>
                }
                isDrawer={true}
                title={I18n.t("mobile.energyMode.infoTitle")}
              />
          }
        />
      </div>
      {toast.status && (
        <ToastMessage
          shouldDisplay={toast.status}
          content={toast.message}
          handleClose={() =>
            setToast({ status: false, message: null, type: null })
          }
          type={toast.type}
          isWeb={false}
          isPermissionDeniedMsg={toast.isPermissionDeniedMsg}
        />
      )}
      <PopUp
        open={showEnergyModePopup}
        handleClose={() => setShowEnergyModePopup(false)}
        content={
          <EnergyPagePopup 
            isIQAirOnly={isIQAirOnly} 
            redirectToProfile={props.redirectToProfile}
            redirectToTariff={props.redirectToTariff}
            closePopup={() => setShowEnergyModePopup(false)}
            energySource={energySource}
            isDarkMode={props.isDarkMode} 
          />  
        }
      />
      <PopUp
        
        open={showEditScenePopup}
        handleClose={handleEditSceneClose}
        slideFromBottom={!props.isWeb}
        fullWidth={!props.isWeb}
        isWeb={props.isWeb}
        content={
          
          <EditScenePopup 
            sceneData={selectedScene}
            isDarkMode={props.isDarkMode}
            closePopup={handleEditSceneClose}
            presets={localPresets}
            availableModes={availableModes}
            IQAirRange={IQAirRange}
            IQAirTemperatureRange={IQAirTemperatureRange}
          />
        }
      />
      <PopUp
      isSchedulePopUp
        open={showSchedulerPopup}
        handleClose={handleSchedulerPopupClose}
        slideFromBottom={!props.isWeb}
        fullWidth={!props.isWeb}
        content={
          <IQAirScheduler
            key={`scheduler-popup-${schedulerSessionId}`}
            {...props}
            showPopupHeader
            popupTitle={I18n.t("mobile.iqAirSetting.schedulerPopupTitle")}
            onClosePopup={handleSchedulerPopupClose}
            scheduleData={popupScheduleData || scheduleData}
            presets={localPresets}
            onScheduleDataChange={setPopupScheduleData}
            handleClick={handleSchedulerPopupClose}
          />
        }
      />
      <PopUp
        open={showDiscardPopup}
        handleClose={() => setShowDiscardPopup(false)}
        content={<CustomPopup
          title={I18n.t("mobile.iqAirSetting.discardChanges")}
          leftAction={() => {setShowDiscardPopup(false)
            navigateBack()}}
          rightAction={() => {
            setShowDiscardPopup(false)
          }}
          newUI={true}
          isDarkMode={props.isDarkMode}
          isWeb={props.isWeb}
          message={I18n.t("mobile.iqAirSetting.discardConfirmMessage")}
          leftButtonText={I18n.t("mobile.iqAirSetting.yes")}
          rightButtonText={I18n.t("mobile.iqAirSetting.no")}
        />}
        isDarkMode={props.isDarkMode}
        disableBackdropClick
      />
    </>
  );
}
